import { redirect } from "react-router";
import AuthManager from "Common/AuthManager";
import { getWorkspaceBootstrap, isWorkspaceNotReadyRoute } from "./workspaceBootstrap";

// One-time workspace bootstrap (party -> industry -> parent channel -> environment ->
// party/switch), previously a useEffect in useSearchBar.tsx guarded by a ref that only survived
// as long as Searchbar itself never remounted. Moved to a route loader — awaited by the router
// before rendering, memoized at module scope (workspaceBootstrap.ts) so it runs exactly once
// regardless of any component's mount lifecycle. Scoped to exactly the routes that used to render
// Layout/Searchbar (everything PrivateRoute-gated) via the "workspace-bootstrap" Route in App.tsx,
// so every one of those routes keeps the same tree depth — no route-specific layout, no
// remount-on-navigation risk.
//
// Mirrors PrivateRoute's own auth check first: PrivateRoute redirects an unauthenticated user to
// /login before Layout/Searchbar ever mounts, so today zero bootstrap calls fire for that case.
// Loaders run before PrivateRoute's render-time check would, so this loader replicates that check
// itself to preserve "no API calls for an unauthenticated hit on a private route."
//
// Lives in its own hook-free module (not App.tsx) so a test can drive it through a real
// createMemoryRouter without importing the whole app's route tree and its enormous import graph.
export const workspaceBootstrapLoader = async ({ request }: { request: Request }) => {
  if (!AuthManager.isAuthenticated()) {
    throw redirect("/login");
  }
  const result = await getWorkspaceBootstrap();
  const pathname = new URL(request.url).pathname;

  // Every failure path of the bootstrap sets redirectTo to /workspace/module/permission/denied,
  // and that route is nested under this same loader. Because the bootstrap result is memoized, the
  // loader on the denied route gets the identical failing result back — so redirecting again from
  // there would redirect the route to itself. React Router has no redirect-loop guard: the
  // navigation never completes (the URL stays on wherever the user came from, e.g. /callback/login),
  // the promise loop starves the main thread, and the tab eventually dies with Chrome's "Aw, Snap"
  // (seen in prod 2026-09-01 for users whose SAMS party had no usable GMS workspace). Once we are
  // already on the target, fall through and let the page render instead.
  if (result.redirectTo && result.redirectTo !== pathname) {
    throw redirect(result.redirectTo);
  }
  // A tab can be sitting on a workspace-not-ready route from a PREVIOUS session (e.g. a failed
  // switch navigated to /workspace/industry/notfound, then the user reloaded). This loader only
  // runs when the subtree is (re-)entered — a full page load being the main case — so a
  // successful bootstrap landing on one of those routes means the error page is stale: the
  // switch worked, the URL is just left over. Nothing downstream ever navigates away (the manual
  // switch handlers only special-case /workspace/preparing), so redeem it here.
  if (result.workspaceLoaded && isWorkspaceNotReadyRoute(pathname)) {
    throw redirect("/gms/performance");
  }
  return result;
};
